import React, { useEffect, useState, useCallback } from 'react';
import {
  Card, Descriptions, Table, Tag, Button, Space, Typography,
  message, Modal, Tabs, Form, InputNumber, Input, Select,
  DatePicker, Divider, Result, Progress, Row, Col, Statistic,
} from 'antd';
import {
  SendOutlined, CheckCircleOutlined, CloseCircleOutlined,
  ArrowLeftOutlined, EditOutlined, PlusOutlined,
  DeleteOutlined,
} from '@ant-design/icons';
import { useNavigate, useParams } from 'react-router-dom';
import { contractApi } from '../../services/api';
import useAuthStore from '../../store/authStore';
import WorkflowTimeline from '../../components/WorkflowTimeline';
import dayjs from 'dayjs';

const { Title, Text } = Typography;

const STATUS_COLORS = {
  '草稿': 'default', '审批中': 'blue', '驳回': 'red',
  '已签署': 'green', '执行中': 'processing', '已完成': 'success', '已终止': 'error',
};

const PAYMENT_METHODS = ['银行转账', '支票', '微信', '支付宝', '现金'];

// Sub-component for payment form to isolate Form.useForm() hooks
function PaymentForm({ contractId, onSuccess }) {
  const [form] = Form.useForm();
  const [addPaymentLoading, setAddPaymentLoading] = useState(false);

  const handleAddPayment = async (values) => {
    setAddPaymentLoading(true);
    try {
      await contractApi.addPayment(contractId, {
        amount: values.amount,
        method: values.method,
        paidAt: values.paidAt ? values.paidAt.toISOString() : new Date().toISOString(),
        notes: values.notes || '',
      });
      message.success('回款记录已添加');
      form.resetFields();
      onSuccess();
    } catch (e) {
      message.error(e?.error || '添加失败');
    } finally {
      setAddPaymentLoading(false);
    }
  };

  return (
    <Form form={form} layout="inline" onFinish={handleAddPayment} style={{ flexWrap: 'wrap', gap: 8 }}>
      <Form.Item name="amount" label="金额" rules={[{ required: true, message: '请输入金额' }]}>
        <InputNumber min={0.01} precision={2} prefix="¥" style={{ width: 140 }} />
      </Form.Item>
      <Form.Item name="method" label="方式" initialValue="银行转账">
        <Select style={{ width: 120 }}>
          {PAYMENT_METHODS.map((m) => (
            <Select.Option key={m} value={m}>{m}</Select.Option>
          ))}
        </Select>
      </Form.Item>
      <Form.Item name="paidAt" label="日期">
        <DatePicker />
      </Form.Item>
      <Form.Item name="notes" label="备注">
        <Input placeholder="备注" style={{ width: 160 }} />
      </Form.Item>
      <Form.Item>
        <Button type="primary" htmlType="submit" loading={addPaymentLoading}
          icon={<PlusOutlined />} style={{ background: '#07c160', borderColor: '#07c160' }}>
          添加回款
        </Button>
      </Form.Item>
    </Form>
  );
}

// Main content component - rendered only when contract data is loaded
function ContractContent({ contract, fetchDetail }) {
  const { id } = useParams();
  const navigate = useNavigate();
  const user = useAuthStore((s) => s.user);
  const [actionLoading, setActionLoading] = useState(false);
  const [activeTab, setActiveTab] = useState('info');

  const paidAmount = contract.paidAmount || 0;
  const unpaidAmount = contract.totalAmount - paidAmount;
  const paymentPct = contract.totalAmount > 0 ? Math.round((paidAmount / contract.totalAmount) * 100) : 0;

  const handleAction = async (action, confirmMsg) => {
    if (confirmMsg) {
      Modal.confirm({
        title: '确认操作',
        content: confirmMsg,
        onOk: () => doAction(action),
      });
    } else {
      await doAction(action);
    }
  };

  const doAction = async (action) => {
    setActionLoading(true);
    try {
      switch (action) {
        case 'submit':
          await contractApi.submit(id);
          message.success('合同已提交审批');
          setActiveTab('workflow');
          break;
        case 'terminate':
          await contractApi.terminate(id);
          message.success('合同已终止');
          break;
        default:
          break;
      }
      fetchDetail();
    } catch (e) {
      message.error(e?.error || '操作失败');
    } finally {
      setActionLoading(false);
    }
  };

  const handleDeletePayment = (paymentId) => {
    Modal.confirm({
      title: '确认删除',
      content: '确定要删除该回款记录吗？',
      okText: '确认', okButtonProps: { danger: true },
      cancelText: '取消',
      onOk: async () => {
        try {
          await contractApi.deletePayment(id, paymentId);
          message.success('已删除');
          fetchDetail();
        } catch (e) {
          message.error(e?.error || '操作失败');
        }
      },
    });
  };

  const itemColumns = [
    { title: '序号', key: 'idx', width: 60, render: (_, __, idx) => idx + 1 },
    {
      title: '产品名称', dataIndex: 'name', key: 'name',
      render: (v, r) => (
        <Space direction="vertical" size={0}>
          <Text strong>{v}</Text>
          {r.product && <Text type="secondary" style={{ fontSize: 12 }}>{r.product.sku}</Text>}
        </Space>
      ),
    },
    { title: '规格', dataIndex: 'spec', key: 'spec', render: (v) => v || '—' },
    { title: '数量', dataIndex: 'quantity', key: 'quantity' },
    { title: '单位', dataIndex: 'unit', key: 'unit' },
    {
      title: '单价', dataIndex: 'unitPrice', key: 'unitPrice',
      render: (v) => (v != null ? v.toLocaleString('zh-CN', { style: 'currency', currency: 'CNY' }) : '—'),
    },
    {
      title: '小计', dataIndex: 'subtotal', key: 'subtotal',
      render: (v) => (v != null ? v.toLocaleString('zh-CN', { style: 'currency', currency: 'CNY' }) : '—'),
    },
  ];

  const paymentColumns = [
    { title: '序号', key: 'idx', width: 60, render: (_, __, idx) => idx + 1 },
    {
      title: '金额', dataIndex: 'amount', key: 'amount',
      render: (v) => (v != null ? v.toLocaleString('zh-CN', { style: 'currency', currency: 'CNY' }) : '—'),
    },
    { title: '方式', dataIndex: 'method', key: 'method' },
    {
      title: '日期', dataIndex: 'paidAt', key: 'paidAt',
      render: (v) => v ? dayjs(v).format('YYYY-MM-DD') : '—',
    },
    { title: '备注', dataIndex: 'notes', key: 'notes', render: (v) => v || '—' },
    {
      title: '操作', key: 'actions', width: 80,
      render: (_, r) => (
        <Button size="small" danger icon={<DeleteOutlined />}
          onClick={() => handleDeletePayment(r.id)}>删除</Button>
      ),
    },
  ];

  const stockOutColumns = [
    { title: '出库编号', dataIndex: 'code', key: 'code' },
    { title: '状态', dataIndex: 'status', key: 'status',
      render: (v) => <Tag color={v === '已发货' ? 'green' : v === '已签收' ? 'blue' : 'orange'}>{v}</Tag>,
    },
    { title: '收货方', dataIndex: 'customerName', key: 'customerName', render: (v) => v || contract.customer?.name },
    {
      title: '产品', key: 'products',
      render: (_, r) => r.items?.map((i) => `${i.product?.name} ×${i.quantity}`).join(', ') || '—',
    },
    { title: '操作人', key: 'operator', render: (_, r) => r.operator?.name || '—' },
    {
      title: '出库日期', dataIndex: 'shippedAt', key: 'shippedAt',
      render: (v) => v ? dayjs(v).format('YYYY-MM-DD') : '未发货',
    },
  ];

  const approvalBanner = contract.status === '审批中' && (
    <div style={{
      background: '#e6f7ff', border: '1px solid #91d5ff', borderRadius: 8,
      padding: '12px 16px', marginBottom: 16, display: 'flex',
      justifyContent: 'space-between', alignItems: 'center',
    }}>
      <span>
        👤 合同处于审批中状态，请 <strong>主管/管理员</strong> 在「工作流进度」中审批通过后，
        系统将自动签署合同并创建出库记录
      </span>
      <Button type="primary" size="small" onClick={() => setActiveTab('workflow')}>
        去审批
      </Button>
    </div>
  );

  const infoTab = (
    <div>
      {approvalBanner}
      <Card size="small" title="合同信息" style={{ marginBottom: 16 }}>
        <Descriptions column={3} bordered size="small">
          <Descriptions.Item label="合同编号">{contract.code}</Descriptions.Item>
          <Descriptions.Item label="客户">{contract.customer?.name}</Descriptions.Item>
          <Descriptions.Item label="联系人">{contract.customer?.contact || '—'}</Descriptions.Item>
          <Descriptions.Item label="合同类型">{contract.type}</Descriptions.Item>
          <Descriptions.Item label="状态">
            <Tag color={STATUS_COLORS[contract.status]}>{contract.status}</Tag>
          </Descriptions.Item>
          <Descriptions.Item label="创建人">{contract.user?.name}</Descriptions.Item>
          <Descriptions.Item label="签署日期">
            {contract.signedAt ? dayjs(contract.signedAt).format('YYYY-MM-DD') : '—'}
          </Descriptions.Item>
          <Descriptions.Item label="开始日期">
            {contract.startAt ? dayjs(contract.startAt).format('YYYY-MM-DD') : '—'}
          </Descriptions.Item>
          <Descriptions.Item label="结束日期">
            {contract.endAt ? dayjs(contract.endAt).format('YYYY-MM-DD') : '—'}
          </Descriptions.Item>
          <Descriptions.Item label="来源">
            {contract.sourceFrom ? `报价 ${contract.sourceFrom}` : '手动创建'}
          </Descriptions.Item>
          <Descriptions.Item label="附件">
            {contract.attachment || '—'}
          </Descriptions.Item>
          <Descriptions.Item label="创建时间">
            {dayjs(contract.createdAt).format('YYYY-MM-DD HH:mm')}
          </Descriptions.Item>
        </Descriptions>
        {contract.notes && (
          <div style={{ marginTop: 16 }}>
            <Text type="secondary">备注：</Text>
            <div style={{ whiteSpace: 'pre-wrap', marginTop: 4 }}>{contract.notes}</div>
          </div>
        )}
      </Card>

      <Card size="small" title="合同金额" style={{ marginBottom: 16 }}>
        <Row gutter={24}>
          <Col span={8}>
            <Statistic title="合同总额" value={contract.totalAmount} precision={2} prefix="¥"
              valueStyle={{ color: '#ff4d4f' }} />
          </Col>
          <Col span={8}>
            <Statistic title="已回款" value={paidAmount} precision={2} prefix="¥"
              valueStyle={{ color: '#3f8600' }} />
          </Col>
          <Col span={8}>
            <Statistic title="待回款" value={unpaidAmount} precision={2} prefix="¥"
              valueStyle={{ color: '#cf1322' }} />
          </Col>
        </Row>
        <Divider />
        <Progress percent={paymentPct} />
      </Card>

      <Card size="small" title="产品明细" style={{ marginBottom: 16 }}>
        <Table columns={itemColumns} dataSource={contract.items}
          rowKey="id" pagination={false} size="small" />
      </Card>
    </div>
  );

  const workflowTab = (
    <Card size="small" title="审批进度">
      {contract.workflowInstanceId ? (
        <WorkflowTimeline instanceId={contract.workflowInstanceId} />
      ) : (
        <Result
          status="info"
          title="未提交审批"
          subTitle="合同尚未提交审批，提交后将创建工单实例跟踪审批进度"
        />
      )}
    </Card>
  );

  const paymentsTab = (
    <div>
      <Card size="small" title="回款明细" style={{ marginBottom: 16 }}>
        <Row gutter={24} style={{ marginBottom: 16 }}>
          <Col span={8}>
            <Statistic title="已回款" value={paidAmount} precision={2} prefix="¥" valueStyle={{ color: '#3f8600' }} />
          </Col>
          <Col span={8}>
            <Statistic title="待回款" value={unpaidAmount} precision={2} prefix="¥" valueStyle={{ color: '#cf1322' }} />
          </Col>
          <Col span={8}>
            <Statistic title="回款比例" value={paymentPct} suffix="%" />
          </Col>
        </Row>
        <Table
          columns={paymentColumns}
          dataSource={contract.payments}
          rowKey="id"
          pagination={false}
          size="small"
        />
      </Card>

      <Card size="small" title="添加回款">
        <PaymentForm contractId={id} onSuccess={fetchDetail} />
      </Card>
    </div>
  );

  const stockOutsTab = (
    <Card size="small" title="出库记录">
      {contract.stockOuts && contract.stockOuts.length > 0 ? (
        <Table
          columns={stockOutColumns}
          dataSource={contract.stockOuts}
          rowKey="id"
          pagination={false}
          size="small"
        />
      ) : (
        <Result
          status={contract.status === '已签署' || contract.status === '执行中' ? 'warning' : 'info'}
          title="暂无出库记录"
          subTitle={
            contract.status === '已签署' || contract.status === '执行中'
              ? '该合同不含实体产品（纯服务合同），跳过出库'
              : '合同签署后将自动生成出库记录'
          }
        />
      )}
    </Card>
  );

  const tabItems = [
    { key: 'info', label: '基本信息', children: infoTab },
    { key: 'workflow', label: '工作流进度', children: workflowTab },
    { key: 'payments', label: '回款记录', children: paymentsTab },
    { key: 'stockOuts', label: '出库记录', children: stockOutsTab },
  ];

  return (
    <div style={{ maxWidth: 1200, margin: '0 auto' }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16 }}>
        <Space>
          <Button icon={<ArrowLeftOutlined />} onClick={() => navigate('/contracts')}>返回</Button>
          <Title level={4} style={{ margin: 0 }}>合同详情</Title>
          <Tag color={STATUS_COLORS[contract.status]} style={{ fontSize: 14, padding: '2px 12px' }}>
            {contract.status}
          </Tag>
          <Text type="secondary">{contract.code}</Text>
        </Space>

        <Space>
          {contract.status === '草稿' && (
            <>
              <Button icon={<EditOutlined />} onClick={() => navigate(`/contracts/new?fromQuotation=${contract.sourceFrom}`)}>
                编辑
              </Button>
              <Button type="primary" icon={<SendOutlined />}
                onClick={() => handleAction('submit', '确认提交审批？提交后将进入审批流程')}
                loading={actionLoading}>提交审批</Button>
            </>
          )}
          {contract.status === '驳回' && (
            <>
              <Button icon={<EditOutlined />} onClick={() => navigate(`/contracts/new?fromQuotation=${contract.sourceFrom}`)}>
                修改重提
              </Button>
              <Button type="primary" icon={<SendOutlined />}
                onClick={() => handleAction('submit', '确认重新提交审批？')}
                loading={actionLoading}>重新提交</Button>
            </>
          )}
          {contract.status === '审批中' && (
            <Button type="primary" icon={<CheckCircleOutlined />}
              onClick={() => {
                setActiveTab('workflow');
                message.info('合同审批中，请主管在"工作流进度"中审批，通过后系统将自动签署并创建出库记录');
              }}
            >
              查看审批进度
            </Button>
          )}
          {['已签署', '执行中'].includes(contract.status) && (
            <Button danger icon={<CloseCircleOutlined />}
              onClick={() => handleAction('terminate', '确认终止该合同？')}
              loading={actionLoading}>终止合同</Button>
          )}
        </Space>
      </div>

      <Tabs activeKey={activeTab} onChange={setActiveTab} items={tabItems} />
    </div>
  );
}

// Loading state component - used when data is being fetched
function LoadingView() {
  return <Card loading />;
}

// Error state component - used when contract not found
function NotFoundView() {
  return <Result status="404" title="合同不存在" />;
}

export default function ContractDetail() {
  const { id } = useParams();
  const [contract, setContract] = useState(null);
  const [loading, setLoading] = useState(true);

  const fetchDetail = useCallback(async () => {
    setLoading(true);
    try {
      const res = await contractApi.get(id);
      setContract(res);
    } catch (e) {
      message.error(e?.error || '获取合同详情失败');
    } finally {
      setLoading(false);
    }
  }, [id]);

  useEffect(() => { fetchDetail(); }, [fetchDetail]);

  // === Auto-polling for workflow completion ===
  useEffect(() => {
    if (!id) return;
    let interval = null;
    const startPolling = async () => {
      interval = setInterval(async () => {
        try {
          const res = await contractApi.get(id);
          if (res && (res.status === '已签署' || res.status === '执行中' || res.status === '已完成')) {
            setContract(res);
            message.success('合同已签署，出库记录已自动创建');
            clearInterval(interval);
            interval = null;
          }
        } catch (_) {
          if (interval) {
            clearInterval(interval);
            interval = null;
          }
        }
      }, 3000);
    };
    startPolling();
    return () => {
      if (interval) {
        clearInterval(interval);
        interval = null;
      }
    };
    // NOTE: Only depends on id to avoid re-creating interval every render
    // id from useParams() is stable, so this effect runs once
  }, [id]);

  // Separate sub-components for loading/not-found to isolate conditional rendering
  if (loading) return <LoadingView />;
  if (!contract) return <NotFoundView />;

  return <ContractContent contract={contract} fetchDetail={fetchDetail} />;
}